/**
 * PDF Export Utility
 * Generates PDF reports for HR and Finance modules
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL;

// Company info
const COMPANY = {
  name: 'Underads Sdn Bhd',
  address: 'No. 123, Jalan Utama, 50000 Kuala Lumpur',
  phone: '03-1234 5678',
  email: 'info@underads.com'
};

// PDF Color Theme
const COLORS = {
  primary: [196, 30, 58],      // #C41E3A - Red/Maroon
  secondary: [100, 100, 100],  // Gray
  dark: [30, 30, 30],          // Dark gray
  light: [245, 245, 245],      // Light gray
  white: [255, 255, 255],
  success: [16, 185, 129],     // Green
  warning: [245, 158, 11],     // Amber
  danger: [239, 68, 68]        // Red
};

// Helper functions
const formatCurrency = (amount, currency = 'RM') => {
  return `${currency} ${Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ms-MY', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const today = () => new Date().toLocaleDateString('ms-MY', { day: '2-digit', month: 'long', year: 'numeric' });

// Add header to PDF
const addHeader = (doc, title, subtitle = '') => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  // Company name
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY.name, 14, 15);
  
  // Report title
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 14, 25);
  
  if (subtitle) {
    doc.setFontSize(9);
    doc.text(subtitle, 14, 31);
  }
  
  // Date on right
  doc.setFontSize(9);
  doc.text(`Dijana: ${today()}`, pageWidth - 14, 15, { align: 'right' });
  
  return 40; // Return Y position after header
};

// Add footer to PDF
const addFooter = (doc, pageNum, totalPages) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  doc.setFillColor(...COLORS.light);
  doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(8);
  doc.text(`Halaman ${pageNum} / ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
  doc.text(COMPANY.name, 14, pageHeight - 6);
  doc.text('Sulit & Rahsia', pageWidth - 14, pageHeight - 6, { align: 'right' });
};

// Add table to PDF
const addTable = (doc, startY, headers, rows, options = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const tableWidth = pageWidth - (margin * 2);
  const colWidths = options.colWidths || headers.map(() => tableWidth / headers.length);
  const rowHeight = 8;
  
  let y = startY;
  
  // Table header
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, y, tableWidth, rowHeight + 2, 'F');
  
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  
  let x = margin + 2;
  headers.forEach((header, i) => {
    doc.text(header, x, y + 6);
    x += colWidths[i];
  });
  
  y += rowHeight + 2;
  
  // Table rows
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  
  rows.forEach((row, rowIndex) => {
    // Check if need new page
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      y = 20;
    }
    
    // Alternating row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(...COLORS.light);
      doc.rect(margin, y, tableWidth, rowHeight, 'F');
    }
    
    x = margin + 2;
    row.forEach((cell, i) => {
      const text = String(cell || '-').substring(0, 30);
      doc.text(text, x, y + 5);
      x += colWidths[i];
    });
    
    y += rowHeight;
  });
  
  return y + 5;
};

// Add section title
const addSectionTitle = (doc, y, title) => {
  doc.setFillColor(...COLORS.light);
  doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 8, 'F');
  
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 16, y + 5.5);
  
  return y + 12;
};

// Add summary box
const addSummaryBox = (doc, y, items) => {
  const boxWidth = (doc.internal.pageSize.getWidth() - 28 - 10) / items.length;
  
  items.forEach((item, i) => {
    const x = 14 + (i * (boxWidth + 5));
    
    doc.setFillColor(...(item.color || COLORS.light));
    doc.roundedRect(x, y, boxWidth, 20, 2, 2, 'F');
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(item.label, x + 4, y + 6);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(String(item.value), x + 4, y + 15);
  });
  
  return y + 28;
};

// ================================
// HR REPORT GENERATORS
// ================================

export const generateHRSummaryReport = async () => {
  try {
    // Fetch data
    const [dashRes, staffRes, perfRes] = await Promise.all([
      fetch(`${API}/api/hr/dashboard`).then(r => r.json()),
      fetch(`${API}/api/staff`).then(r => r.json()),
      fetch(`${API}/api/hr/performance`).then(r => r.json())
    ]);
    
    const doc = new jsPDF();
    let y = addHeader(doc, 'LAPORAN RINGKASAN SUMBER MANUSIA (HR)', `Bulan: ${new Date().toLocaleDateString('ms-MY', { month: 'long', year: 'numeric' })}`);
    
    const s = dashRes.summary || {};
    
    // Summary boxes
    y = addSummaryBox(doc, y, [
      { label: 'Jumlah Staf', value: s.total_staff || 0, color: COLORS.light },
      { label: 'Dinilai', value: s.total_evaluated || 0, color: [219, 234, 254] },
      { label: 'Amaran Aktif', value: s.active_warnings || 0, color: s.active_warnings > 0 ? [254, 226, 226] : COLORS.light },
      { label: 'Cuti Pending', value: s.pending_leaves || 0, color: [254, 243, 199] }
    ]);
    
    // Staff list
    y = addSectionTitle(doc, y, 'SENARAI STAF AKTIF');
    
    const activeStaff = staffRes.filter(s => s.status === 'Active');
    const staffRows = activeStaff.slice(0, 30).map(s => [
      s.name,
      s.department || '-',
      s.position || '-',
      formatCurrency(s.basic_salary),
      s.join_date ? formatDate(s.join_date) : '-'
    ]);
    
    y = addTable(doc, y, ['Nama', 'Jabatan', 'Jawatan', 'Gaji Asas', 'Tarikh Mula'], staffRows, {
      colWidths: [50, 35, 35, 30, 30]
    });
    
    // Performance summary
    if (perfRes.length > 0) {
      if (y > 220) {
        doc.addPage();
        y = 20;
      }
      
      y = addSectionTitle(doc, y, 'RINGKASAN PENILAIAN PRESTASI');
      
      const perfRows = perfRes.slice(0, 15).map(p => [
        p.staff_name,
        p.period,
        `${p.overall_score}%`,
        p.rating,
        p.needs_training ? 'Ya' : '-'
      ]);
      
      y = addTable(doc, y, ['Staf', 'Tempoh', 'Skor', 'Penilaian', 'Perlu Latihan'], perfRows, {
        colWidths: [50, 30, 25, 45, 30]
      });
    }
    
    // Add footer to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, i, totalPages);
    }
    
    doc.save(`Laporan_HR_${new Date().toISOString().split('T')[0]}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

export const generateAttendanceReport = async (month) => {
  try {
    const currentMonth = month || new Date().toISOString().slice(0, 7);
    const [attRes, staffRes] = await Promise.all([
      fetch(`${API}/api/hr/attendance?month=${currentMonth}`).then(r => r.json()),
      fetch(`${API}/api/staff`).then(r => r.json())
    ]);
    
    const doc = new jsPDF();
    let y = addHeader(doc, 'LAPORAN KEHADIRAN STAF', `Bulan: ${currentMonth}`);
    
    // Attendance summary
    const summary = {
      hadir: attRes.filter(a => a.status === 'Hadir').length,
      cuti: attRes.filter(a => a.status === 'Cuti').length,
      mc: attRes.filter(a => a.status === 'MC').length,
      lewat: attRes.filter(a => a.status === 'Lewat').length,
      tidak_hadir: attRes.filter(a => a.status === 'Tidak Hadir').length
    };
    
    y = addSummaryBox(doc, y, [
      { label: 'Hadir', value: summary.hadir, color: [209, 250, 229] },
      { label: 'Cuti', value: summary.cuti, color: [219, 234, 254] },
      { label: 'MC', value: summary.mc, color: [254, 243, 199] },
      { label: 'Lewat', value: summary.lewat, color: [254, 215, 170] },
      { label: 'Tidak Hadir', value: summary.tidak_hadir, color: [254, 226, 226] }
    ]);
    
    y = addSectionTitle(doc, y, 'REKOD KEHADIRAN');
    
    const attRows = attRes.slice(0, 50).map(a => [
      formatDate(a.date),
      a.staff_name,
      a.check_in || '-',
      a.check_out || '-',
      a.status
    ]);
    
    y = addTable(doc, y, ['Tarikh', 'Staf', 'Masuk', 'Keluar', 'Status'], attRows, {
      colWidths: [30, 60, 25, 25, 40]
    });
    
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, i, totalPages);
    }
    
    doc.save(`Laporan_Kehadiran_${currentMonth}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

// ================================
// FINANCE REPORT GENERATORS
// ================================

export const generateFinanceSummaryReport = async () => {
  try {
    const [kpiRes, revenueRes, expenseRes, cashflowRes] = await Promise.all([
      fetch(`${API}/api/dashboard/kpi`).then(r => r.json()),
      fetch(`${API}/api/revenue`).then(r => r.json()),
      fetch(`${API}/api/expenses`).then(r => r.json()),
      fetch(`${API}/api/dashboard/cashflow`).then(r => r.json())
    ]);
    
    const doc = new jsPDF();
    let y = addHeader(doc, 'LAPORAN RINGKASAN KEWANGAN', `Bulan: ${new Date().toLocaleDateString('ms-MY', { month: 'long', year: 'numeric' })}`);
    
    // KPI Summary
    y = addSummaryBox(doc, y, [
      { label: 'Pendapatan MTD', value: formatCurrency(kpiRes.total_revenue_mtd, 'USD'), color: [209, 250, 229] },
      { label: 'Perbelanjaan MTD', value: formatCurrency(kpiRes.total_expenses_mtd, 'USD'), color: [254, 226, 226] },
      { label: 'Untung/Rugi', value: formatCurrency(kpiRes.net_profit_loss_mtd, 'USD'), color: kpiRes.net_profit_loss_mtd >= 0 ? [209, 250, 229] : [254, 226, 226] },
      { label: 'Baki Tunai', value: formatCurrency(kpiRes.current_cash_balance, 'USD'), color: [219, 234, 254] }
    ]);
    
    // Revenue breakdown
    y = addSectionTitle(doc, y, 'PENDAPATAN (TOP 20)');
    
    const revenueRows = revenueRes.slice(0, 20).map(r => [
      formatDate(r.date),
      r.revenue_source,
      r.channel,
      formatCurrency(r.amount, 'USD'),
      r.payment_method
    ]);
    
    y = addTable(doc, y, ['Tarikh', 'Sumber', 'Channel', 'Jumlah', 'Kaedah'], revenueRows, {
      colWidths: [28, 35, 35, 40, 40]
    });
    
    // Expenses breakdown
    if (y > 180) {
      doc.addPage();
      y = 20;
    }
    
    y = addSectionTitle(doc, y, 'PERBELANJAAN (TOP 20)');
    
    const expenseRows = expenseRes.slice(0, 20).map(e => [
      formatDate(e.date),
      e.expense_category,
      e.sub_category,
      formatCurrency(e.amount, 'USD'),
      e.payment_from
    ]);
    
    y = addTable(doc, y, ['Tarikh', 'Kategori', 'Sub-Kategori', 'Jumlah', 'Dari'], expenseRows, {
      colWidths: [28, 35, 40, 40, 35]
    });
    
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, i, totalPages);
    }
    
    doc.save(`Laporan_Kewangan_${new Date().toISOString().split('T')[0]}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

export const generatePayrollReport = async () => {
  try {
    const payrollRes = await fetch(`${API}/api/payroll/summary`).then(r => r.json());
    
    const doc = new jsPDF('landscape');
    let y = addHeader(doc, 'LAPORAN GAJI BULANAN', `Bulan: ${new Date().toLocaleDateString('ms-MY', { month: 'long', year: 'numeric' })}`);
    
    // Summary
    y = addSummaryBox(doc, y, [
      { label: 'Jumlah Staf', value: payrollRes.total_staff, color: COLORS.light },
      { label: 'Gaji Kasar', value: formatCurrency(payrollRes.total_gross), color: [219, 234, 254] },
      { label: 'Potongan', value: formatCurrency(payrollRes.total_deductions), color: [254, 243, 199] },
      { label: 'Gaji Bersih', value: formatCurrency(payrollRes.total_net_pay), color: [209, 250, 229] },
      { label: 'Kos Majikan', value: formatCurrency(payrollRes.total_employer_cost), color: [254, 226, 226] }
    ]);
    
    y = addSectionTitle(doc, y, 'SENARAI GAJI STAF');
    
    const staffRows = (payrollRes.staff_list || []).map(s => [
      s.name,
      formatCurrency(s.basic_salary),
      formatCurrency(s.gross_salary),
      formatCurrency(s.epf_employee),
      formatCurrency(s.socso_employee),
      formatCurrency(s.total_deductions),
      formatCurrency(s.net_pay)
    ]);
    
    y = addTable(doc, y, ['Nama', 'Gaji Asas', 'Kasar', 'EPF', 'SOCSO', 'Potongan', 'Bersih'], staffRows, {
      colWidths: [55, 35, 35, 30, 30, 35, 35]
    });
    
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, i, totalPages);
    }
    
    doc.save(`Laporan_Gaji_${new Date().toISOString().split('T')[0]}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

export const generateAssetReport = async () => {
  try {
    const [assetsRes, summaryRes] = await Promise.all([
      fetch(`${API}/api/assets`).then(r => r.json()),
      fetch(`${API}/api/assets/summary/dashboard`).then(r => r.json())
    ]);
    
    const doc = new jsPDF();
    let y = addHeader(doc, 'LAPORAN ASET SYARIKAT', `Tarikh: ${today()}`);
    
    // Summary
    y = addSummaryBox(doc, y, [
      { label: 'Jumlah Aset', value: summaryRes.total_assets, color: COLORS.light },
      { label: 'Nilai Pembelian', value: formatCurrency(summaryRes.total_purchase_value), color: [219, 234, 254] },
      { label: 'Nilai Semasa', value: formatCurrency(summaryRes.total_current_value), color: [209, 250, 229] },
      { label: 'Susut Nilai', value: formatCurrency(summaryRes.total_depreciation), color: [254, 243, 199] }
    ]);
    
    y = addSectionTitle(doc, y, 'SENARAI ASET');
    
    const assetRows = assetsRes.map(a => [
      a.name,
      a.category,
      formatCurrency(a.purchase_price),
      formatCurrency(a.current_value),
      a.status,
      formatDate(a.warranty_expiry)
    ]);
    
    y = addTable(doc, y, ['Nama', 'Kategori', 'Harga Beli', 'Nilai Semasa', 'Status', 'Waranti'], assetRows, {
      colWidths: [45, 30, 30, 30, 25, 25]
    });
    
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, i, totalPages);
    }
    
    doc.save(`Laporan_Aset_${new Date().toISOString().split('T')[0]}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

// Export screen as PDF (for any page)
export const exportScreenToPDF = async (elementId, filename = 'report') => {
  try {
    const element = document.getElementById(elementId);
    if (!element) throw new Error('Element not found');
    
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });
    
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    return { success: true };
  } catch (error) {
    console.error('Screen capture error:', error);
    return { success: false, error: error.message };
  }
};

export default {
  generateHRSummaryReport,
  generateAttendanceReport,
  generateFinanceSummaryReport,
  generatePayrollReport,
  generateAssetReport,
  exportScreenToPDF
};
